import { z } from 'zod';
import { releaseId, ruleId, ruleVersionId, sourceOfLawId } from '../ids.js';
import { jurisdiction } from '../enums.js';
/**
 * Garnet Core domain (Core PRD §5–§6). The condition language is a typed, inspectable
 * predicate — decision tables for classification, guarded expressions for requirements
 * (Core PRD §21 default). No free text, no inference, no model (Invariant I2). The
 * evaluator lives in services/garnet-core; these are the shared shapes consumers see.
 */
export const coreDomain = z.enum([
    'medical',
    'automotive',
    'electronics',
    'aeronautics',
    'corporate',
    'tax_customs',
    'sanctions_export',
    'insurance',
]);
export const outputType = z.enum(['classification', 'requirement', 'applicability', 'value']);
export const ruleStatus = z.enum(['draft', 'in_review', 'published', 'superseded', 'retired']);
/**
 * Stable, citation-shaped identifier: {JURIS}-{INSTRUMENT}-{LOCUS}-{SEQ}, e.g.
 * EU-MDR-AnnexVIII-R12. Permanent across versions; never reused after retirement.
 */
export const ruleIdentifier = z.string().regex(/^[A-Z]{2,8}(-[A-Za-z0-9.]+){2,4}$/);
// ── Typed inputs ───────────────────────────────────────────────────────────────────────
export const factorType = z.enum(['boolean', 'choice', 'number', 'code']);
export const factor = z.object({
    key: z.string().min(1),
    type: factorType,
    label: z.string(),
    /** For choice factors: the closed set of values. */
    choices: z.array(z.string()).nullable(),
});
const factorValue = z.union([z.boolean(), z.number(), z.string()]);
export const factorInputs = z.record(z.string(), factorValue);
// ── Predicates (guarded expressions) ───────────────────────────────────────────────────
export const comparator = z.enum(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'matches_code']);
export const predicate = z.lazy(() => z.discriminatedUnion('op', [
    z.object({
        op: z.literal('test'),
        factor: z.string(),
        cmp: comparator,
        value: z.union([factorValue, z.array(factorValue)]),
    }),
    z.object({ op: z.literal('all'), of: z.array(predicate) }),
    z.object({ op: z.literal('any'), of: z.array(predicate) }),
    z.object({ op: z.literal('not'), of: predicate }),
]));
// ── Conditions: decision table or guarded expression ───────────────────────────────────
export const decisionTableCondition = z.object({
    form: z.literal('table'),
    factors: z.array(factor).min(1),
    /** Rows are evaluated in order; the first full match wins (most-precise-first authoring). */
    rows: z.array(z.object({
        when: predicate,
        output: z.unknown(),
        note: z.string().nullable(),
    })),
});
export const guardedExpressionCondition = z.object({
    form: z.literal('guarded'),
    factors: z.array(factor).min(1),
    clauses: z.array(z.object({
        guard: predicate,
        output: z.unknown(),
        note: z.string().nullable(),
    })),
});
export const condition = z.discriminatedUnion('form', [
    decisionTableCondition,
    guardedExpressionCondition,
]);
// ── Rule, version, source of law ───────────────────────────────────────────────────────
export const SourceOfLaw = z.object({
    id: sourceOfLawId,
    citation: z.string().min(1),
    kind: z.enum(['statute', 'regulation', 'directive', 'annex', 'standard', 'guidance']),
    jurisdiction,
    effective_from: z.string().datetime().nullable(),
    url: z.string().url().nullable(),
    watch: z.enum(['stable', 'monitoring', 'changed']),
    last_checked: z.string().datetime().nullable(),
});
export const RuleVersion = z.object({
    id: ruleVersionId,
    rule_id: ruleId,
    version: z.number().int().positive(),
    condition,
    output: z.unknown(),
    /** Mandatory: a version with no citation cannot advance past draft (Core PRD §7.1). */
    citations: z.array(sourceOfLawId).min(1),
    effective_from: z.string().datetime().nullable(),
    effective_to: z.string().datetime().nullable(),
    authored_by: z.string(),
    approved_by: z.string().nullable(),
    rationale: z.string(),
    tests: z.array(z.object({
        name: z.string(),
        inputs: factorInputs,
        expect: z.union([
            z.object({ kind: z.literal('output'), output: z.unknown() }),
            z.object({ kind: z.literal('abstain') }),
        ]),
    })),
});
export const Rule = z.object({
    id: ruleId,
    rule_identifier: ruleIdentifier,
    jurisdiction,
    domain: coreDomain,
    topic: z.string().min(1),
    output_type: outputType,
    title: z.string().min(1),
    current_version: ruleVersionId.nullable(),
    status: ruleStatus,
});
// ── Coverage (Core PRD §5.3, §8) ───────────────────────────────────────────────────────
export const coverageState = z.enum(['covered', 'thin', 'empty']);
export const CoverageCell = z.object({
    jurisdiction,
    domain: coreDomain,
    topic: z.string(),
    state: coverageState,
    rule_count: z.number().int().nonnegative(),
    gate: z.enum(['answer', 'abstain']),
    owner: z.string().nullable(),
});
// ── Releases (Core PRD §15) ────────────────────────────────────────────────────────────
export const Release = z.object({
    id: releaseId,
    name: z.string().regex(/^\d{4}\.\d{2}\.\d+$/, 'release names are CalVer: YYYY.MM.seq'),
    cut_at: z.string().datetime(),
    cut_by: z.string(),
    /** Immutable snapshot: rule version ids frozen into this release. */
    rule_versions: z.array(ruleVersionId),
    changelog: z.string(),
    /** Detached signature so a consumer can verify the release it pinned (Core PRD §17.2). */
    signature: z.object({ key_id: z.string(), sig: z.string() }).nullable(),
    rolled_back: z.boolean(),
});
// ── Serving contract (Core PRD §11) ────────────────────────────────────────────────────
export const servingAnswer = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('answer'),
        result: z.unknown(),
        rule_identifiers: z.array(ruleIdentifier).min(1),
        citations: z.array(z.string()).min(1),
        release: z.string(),
        crosswalk_version: z.string().nullable(),
    }),
    z.object({
        kind: z.literal('abstain'),
        reason: z.enum(['out_of_scope', 'ambiguous', 'conflict']),
        detail: z.string(),
        release: z.string(),
    }),
    z.object({
        kind: z.literal('no_coverage'),
        cell: z.object({ jurisdiction, domain: coreDomain, topic: z.string() }),
        release: z.string(),
    }),
]);
export const classifyQuery = z.object({
    jurisdiction,
    nomenclature: z.object({ emdn: z.string().nullable(), gmdn: z.string().nullable() }).partial(),
    characteristics: factorInputs,
    intended_purpose: z.string().nullable(),
});
export const QueryLog = z.object({
    consumer: z.string(),
    query_kind: z.enum(['classify', 'requirements', 'applicability', 'value', 'coverage']),
    query: z.unknown(),
    release: z.string(),
    answer_kind: z.enum(['answer', 'abstain', 'no_coverage']),
    rule_identifiers: z.array(z.string()),
    latency_ms: z.number().nonnegative(),
    residency_region: z.string(),
    at: z.string().datetime(),
});
//# sourceMappingURL=rule.js.map